import { apolloClient, CREATE_MAESTRO_USER, GET_RECRUITER_LIST, GET_RECRUITER_PROFILE, RESEND_MAESTRO_INVITE, UPDATE_RECRUITER_ROLE } from '~/common/apollo'
import { markNotified } from '~/common/helpers/user-message.helper'
import { action, observable, store, computed, runInAction } from '~/common/mobx.decorator'
import { genericTableStore } from '~/components/generic-table/generic-table.store'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { Chip, Typography, useTheme, Box, IconButton, Button, Grid } from '@material-ui/core'
import { HEADER_SORT_DATA_TYPE, LIMIT_DATA_INNER_TABLE, PATHS } from '~/common/constants'
import { userInfoPath } from '~/features/recruiter-v2/shell/maestro-version.helper'
import { UserTitle } from '~/components/user-title/user-title.component'
import React from 'react'
import { masterStore, notifyStore, authStore, routerStore } from '~/stores'
import { RoleCell } from '~/features/recruiter-role/component/role-cell/role-cell.component'
import { UserSettingSvg } from '~/components/icons'
import { uniqBy } from 'lodash'
import { autoFieldTagStyle } from '~/features/recruiter-role/recruiter-role.style'
import { UserAvatar } from '~/components/user-avatar/user-avatar.component'
import { columnTooltip } from '~/features/recruiter-role/component/role-guide/column-info-card.component'
import { TABLE_NAMES } from '~/common/constants/table-name.constant'
import { INITIAL_TABLE_PAGE_SIZE } from '~/common/constants/table.constant'
import { dateFormat } from '~/common/helpers/time.helper'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import { ACCESS_STATE, canAdminSendInvite, getAccessState, isAccessBlocked, shouldPromptInviteAfterRoleAssign } from '~/features/recruiter-role/access-state'
import { createFullName, normalizeEmployerPopupData, normalizeEmployerRelations } from './employer-name.helper'

// Re-exported: other screens import these names from here.
export { createFullName, normalizeEmployerPopupData, normalizeEmployerRelations }

export const employerDisplayName = (employer) => {
	if (!employer) return 'this user'

	const normalized = normalizeEmployerPopupData(employer)

	if (normalized.firstName || normalized.lastName) {
		return createFullName(normalized.firstName, normalized.lastName)
	}

	if (normalized.fullName && normalized.fullName !== 'Unknown User') {
		return normalized.fullName
	}

	return normalized.email || employer.email || 'this user'
}

const employerNameFromConflictMessage = (message) => message?.replace(/ already exists in Maestro$/i, '')?.trim() || 'This person'

@store()
class RecruiterRoleStore {
	@observable recruiters = []
	@observable pageInfo
	@observable openPopup = false
	@observable openCreatePopup = false
	@observable popupData = {}
	@observable employerKeyword = ''
	@observable employerOptions = []
	@observable outerTotalCount = 0
	@observable isImpersonating = false
	@observable impersonatedRecruiter = null
	// Employer id currently having an invite sent, so the row's button can be
	// disabled. A second click would mail a second password and invalidate the
	// first, which is precisely the lockout this feature exists to prevent.
	@observable resendingInviteFor = null
	// The keyword the list is currently filtered by. Actions on this screen refetch
	// when they finish, and without this they refetch the *unfiltered* list - so
	// resending an invite to the one person you searched for threw away the search
	// and dropped you back into all 102 rows.
	lastKeyword = undefined

	@computed
	get hasNextPage() {
		return this.pageInfo?.hasNextPage || false
	}

	@computed
	get endCursor() {
		return this.pageInfo?.endCursor
	}

	@computed
	get filterFields() {
		return genericTableStore.getFiltersValueForTable(TABLE_NAMES.ROLE)
	}

	@computed
	get sortOrder() {
		return genericTableStore.sortOrder
	}

	@computed
	get sortField() {
		return genericTableStore.sortField
	}

	@computed
	get impersonatedRecruiterId() {
		return localStorage.getItem('impersonateRecruiterId')
	}

	@action
	setEmployerKeyword = (keyword) => {
		this.employerKeyword = keyword
	}

	@action
	togglePopup = () => {
		this.openPopup = !this.openPopup
	}

	@action
	toggleCreatePopup = () => {
		this.openCreatePopup = !this.openCreatePopup
	}

	@action
	updatePopupData(data) {
		const supporters = normalizeEmployerRelations(data?.supporters)
		this.popupData = normalizeEmployerPopupData({ ...data, supporters })
		this.employerKeyword = ''
		const defaultData = supporters.map((item) => ({ ...item, label: `${item.firstName} ${item.lastName}`, value: item.id }))
		this.employerOptions = uniqBy([...defaultData, ...this.employerOptions], (worker) => worker.id)
	}

	// Collision path: close Add user, hydrate popupData, then open Assign role in one
	// MobX transaction. Async @action only wraps code before the first await — state
	// updates after fetch must run inside runInAction or React can render stale data.
	@action
	openAssignRoleDialog(employer) {
		if (!employer?.id) return

		this.openCreatePopup = false
		this.updatePopupData(employer)
		this.openPopup = true
	}

	get tableHeader() {
		const { t } = useTranslation()
		const theme = useTheme()

		const COLUMNS = [
			{
				key: 'fullName',
				dataKey: 'fullName',
				title: t('NAME'),
				resizable: false,
				flexGrow: 1,
				minWidth: 350,
				sortable: true,
				sortDataType: HEADER_SORT_DATA_TYPE.alphabet,
				tooltip: columnTooltip('fullName'),
				cell: ({ rowData }) => {
					// `linkTo` overrides UserTitle's default candidate destination: these
					// rows are internal users, not candidates, so `/candidate_info/<id>`
					// would resolve to nothing.
					return <UserTitle useForJobApplicant={false} linkTo={userInfoPath(rowData?.id)} color={theme.palette.primary.main} data={rowData} />
				},
			},
			{
				key: 'roles',
				dataKey: 'roles',
				title: t('ROLE'),
				resizable: false,
				flexGrow: 1,
				// 235, not 200: two chips - the widest pair being ADMIN plus ACCOUNT
				// MANAGER - need roughly 210px plus the cell's own padding. At 200 the
				// second pill had to shrink, and its label wrapped onto a second line.
				minWidth: 235,
				sortable: true,
				sortDataType: HEADER_SORT_DATA_TYPE.multiSelect,
				// Internal roles only. `recruiterFullRoles` also carries the two
				// client-portal scopes, and the BE list is filtered to
				// MAESTRO_EMPLOYER_SCOPES — so those were filter options that could
				// never match a row.
				sortOptions: masterStore?.maestroRoles,
				tooltip: columnTooltip('roles'),
				cell: ({ rowData }) => (
					<Box display="flex" alignItems="center" height="100%">
						<RoleCell data={rowData?.roles} />
					</Box>
				),
			},
			{
				key: 'supporters',
				dataKey: 'supporters',
				title: t('TEMPORARY_SUPPORT'),
				// The two things people get wrong about this column, in the place they
				// read it. The name promises an expiry that is not stored anywhere, and
				// the relation runs the opposite way to what most people assume.
				tooltip:
					'The people listed here can see this recruiter’s candidates — the row is the recruiter being covered, the list is who covers them. Despite the name there is no end date: access lasts until it is removed by hand.',
				resizable: false,
				flexGrow: 1.7,
				minWidth: 375,
				sortable: false,
				tooltip: columnTooltip('supporters'),
				cell: ({ rowData }) => {
					const data = rowData?.supporters
					if (!data?.length) return ''

					// Hiển thị tất cả nếu có 3 người trở xuống
					if (data?.length <= 3) {
						return (
							<Box display="flex" height="100%" alignItems="center">
								{data?.map((item, index) => (
									<Chip
										key={index}
										style={{ border: 'none' }}
										label={
											<div css={autoFieldTagStyle}>
												<UserAvatar
													size={16}
													fontSize={8}
													customHeight={'25px'}
													customWidth={'25px'}
													firstName={item?.firstName}
													lastName={item?.lastName}
													src={item?.fileUrl}
												/>
												<Typography variant="body3">{`${item?.firstName} ${item?.lastName}`}</Typography>
											</div>
										}
									/>
								))}
							</Box>
						)
					}
					// Hiển thị 3 người đầu tiên + số người còn lại
					else {
						const splitData = data?.slice(0, 3)
						const remainingCount = data.length - 3

						return (
							<Box position="relative" width="100%" height="100%">
								<Grid container spacing={0} style={{ height: '100%', alignItems: 'center' }}>
									<Grid item xs={10}>
										<Box display="flex" alignItems="center" height="100%" style={{ overflow: 'hidden' }}>
											<Box display="flex" style={{ flexWrap: 'nowrap', overflow: 'hidden', width: '100%', height: '100%', alignItems: 'center' }}>
												{splitData?.map((item, index) => (
													<Chip
														key={index}
														style={{
															border: 'none',
															margin: '0 2px',
															maxWidth: '32%',
															overflow: 'hidden',
														}}
														label={
															<div css={autoFieldTagStyle} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
																<UserAvatar
																	size={16}
																	fontSize={8}
																	customHeight={'25px'}
																	customWidth={'25px'}
																	firstName={item?.firstName}
																	lastName={item?.lastName}
																	src={item?.fileUrl}
																/>
																<Typography variant="body3" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
																	{`${item?.firstName} ${item?.lastName}`}
																</Typography>
															</div>
														}
													/>
												))}
											</Box>
										</Box>
									</Grid>
									<Grid item xs={2} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												border: '1px solid #E6E6E6',
												borderRadius: '50%',
												width: '28px',
												height: '28px',
												fontSize: '12px',
												marginRight: '10px',
											}}
										>
											+{remainingCount}
										</div>
									</Grid>
								</Grid>
							</Box>
						)
					}
				},
			},
			{
				key: 'signIn',
				dataKey: 'signIn',
				// Literal rather than a key: SIGN_IN is already taken at the top level of
				// care.json by the login screen's all-caps "SIGN IN", and a duplicate key
				// would resolve to that. Same pattern as the View as Recruiter column.
				title: t('Sign-in'),
				resizable: false,
				sortable: false,
				// Sized for the longest label, "Signed in 08/11/2026", and no wider -
				// the width this column takes comes out of Role and Temporary support.
				minWidth: 175,
				flexGrow: 0.5,
				tooltip:
					'Whether this person has actually signed in to Maestro. Someone who never has, and whose invitation has expired or was never sent, cannot get in until you send them one.',
				cell: ({ rowData }) => {
					const user = rowData?.user
					const state = getAccessState(user)

					const label = {
						[ACCESS_STATE.signedIn]: user?.lastSignInAt ? `${t('Signed in')} ${dateFormat(user.lastSignInAt)}` : t('Signed in'),
						[ACCESS_STATE.awaiting]: user?.welcomeEmailSentAt ? `${t('Invited')} ${dateFormat(user.welcomeEmailSentAt)}` : t('Invited'),
						[ACCESS_STATE.expired]: t('Invite expired'),
						[ACCESS_STATE.notInvited]: t('Never invited'),
					}[state]

					return (
						<Box display="flex" alignItems="center" height="100%">
							<Typography variant="body2" color={isAccessBlocked(user) ? 'error' : 'textSecondary'}>
								{label}
							</Typography>
						</Box>
					)
				},
			},
			{
				key: 'action',
				dataKey: 'action',
				title: t('ACTION'),
				width: 120,
				resizable: false,
				sortable: false,
				align: 'center',
				permission: true,
				tooltip: columnTooltip('action'),
				cell: ({ rowData }) => {
					if (!rowData?.availableToChangeRoles || !authStore.canAccessRoleScreen) {
						return null
					}

					const neverInvited = getAccessState(rowData?.user) === ACCESS_STATE.notInvited
					const showInviteAction = authStore.isAdmin && canAdminSendInvite(rowData?.user)

					return (
						<Box display="flex" justifyContent="center" alignItems="center" height="100%">
							<IconButton
								size="small"
								title={t('Assign role')}
								onClick={() => {
									this.updatePopupData(rowData)
									this.togglePopup()
								}}
							>
								<UserSettingSvg />
							</IconButton>
							{/*
							 * Admin only. Never invited and expired need a first send or a
							 * fresh credential; awaiting covers the lost-email case while
							 * the temporary password is still valid. Signed-in rows get no
							 * mail icon — they should use Forgot password instead.
							 */}
							{showInviteAction && (
								<IconButton
									size="small"
									title={neverInvited ? t('Send invite') : t('Resend invite')}
									disabled={this.resendingInviteFor === rowData?.id}
									onClick={() => this.resendInvite(rowData)}
								>
									<MailOutlineIcon fontSize="small" />
								</IconButton>
							)}
						</Box>
					)
				},
			},
			{
				key: 'viewAsRecruiter',
				dataKey: 'viewAsRecruiter',
				title: t('View as Recruiter'),
				resizable: false,
				align: 'center',
				tooltip: columnTooltip('viewAsRecruiter'),
				/*
				 * "View as Recruiter" at the theme's button face (15px/600) measures
				 * ~125px, so the button is ~157px wide with its padding and the cell
				 * adds 16px either side. At the old 150 the button had 118px to sit in,
				 * shrank, and wrapped to two lines inside a fixed 32px height.
				 */
				minWidth: 210,
				flexGrow: 0.75,
				cell: ({ rowData }) => {
					// Only show for ADMIN and MANAGER roles
					if (!authStore.isAdmin && !authStore.isAccountManager) {
						return null
					}

					// Don't show button for non-recruiter roles
					if (!rowData.roles.some((role) => role.scope === 'maestro_recruiter')) {
						return null
					}

					return (
						<Box display="flex" justifyContent="center" alignItems="center" height="100%">
							{/*
							 * `sizeSmall` is a fixed 32px in the theme, so a wrapped label spills
							 * out of the button instead of growing it. Keep it on one line and
							 * stop the flex parent shrinking it below its content.
							 */}
							<Button
								variant="containedPrimary"
								type="button"
								size="small"
								style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
								onClick={() => this.startImpersonation(rowData.id)}
							>
								{t('View as Recruiter')}
							</Button>
						</Box>
					)
				},
			},
		]

		return COLUMNS
	}

	@action
	resetFetchedData = () => {
		this.recruiters = []
		this.pageInfo = null
		this.outerTotalCount = 0
	}

	@action
	initializeDefaultSort = () => {
		// Force reset sort to default for this tab
		genericTableStore.onUpdateSort('fullName', 'asc')
	}

	@action
	updateRole = async (id, role, supporterIds) => {
		const employerId = id || this.popupData?.id
		if (!employerId) {
			notifyStore.error("We couldn't update that user's role. Please close the dialog and try again.")
			return
		}

		try {
			const variables = authStore.canUpdateUserRole
				? {
						id: employerId,
						roles: [role],
						supporterIds,
				  }
				: {
						id: employerId,
						supporterIds,
				  }

			await apolloClient().mutate({
				mutation: UPDATE_RECRUITER_ROLE,
				variables,
			})

			const assignedEmployer = { ...this.popupData }

			this.togglePopup()
			await this.fetchRecruiters()
			const roleName = masterStore.recruiterRoles.find((item) => item.value === role)?.label

			const successMessage = authStore.canUpdateUserRole ? (
				<div>
					You have successfully assign <b>{assignedEmployer.fullName}</b> to <b>{roleName}</b>
				</div>
			) : (
				<div>
					You have successfully updated temporary support for <b>{assignedEmployer.fullName}</b>
				</div>
			)

			notifyStore.success(successMessage)

			if (authStore.isAdmin && authStore.canUpdateUserRole && shouldPromptInviteAfterRoleAssign(assignedEmployer?.user)) {
				this.resendInvite(assignedEmployer)
			}
		} catch (e) {
			console.error(e)
		}
	}

	// Creates a user that lives only in Maestro - nothing is sent to the ATS.
	//
	// `coversEmployer` becomes `supportingIds`, the inverse of the Temporary Support
	// list edited in the Assign-role dialog. It is required rather than optional: a
	// Maestro-local user never owns a candidate desk, because Worker.employerId is
	// only ever written by ATS sync, so a user created without it signs in to an
	// empty screen.
	//
	// Unlike updateRole, failures are surfaced. Creating a user has an expected
	// failure the admin needs to see - the email already belongs to someone in this
	// tenant - and swallowing it would look like the button did nothing.
	@action
	createUser = async ({ firstName, lastName, email, phone, title, role, coversEmployer }) => {
		try {
			const { data } = await apolloClient().mutate({
				mutation: CREATE_MAESTRO_USER,
				variables: {
					firstName,
					lastName,
					email,
					phone: phone || null,
					title: title || null,
					roles: [role],
					supportingIds: coversEmployer?.map((item) => item?.id) || [],
				},
			})

			this.toggleCreatePopup()
			this.resetFetchedData()
			// Explicitly unfiltered, unlike the other actions on this screen. Add user
			// is usually reached *from* a search that found nothing - that is the whole
			// SUP-3047 story - so keeping that keyword would hide the person just
			// created behind the search that failed to find them.
			await this.fetchRecruiters('')
			await this.fetchAllRecruiters()

			const roleName = masterStore.recruiterRoles.find((item) => item.value === role)?.label
			const fullName = createFullName(data?.createMaestroUser?.firstName, data?.createMaestroUser?.lastName)

			notifyStore.success(
				<div>
					<b>{fullName}</b> was added as <b>{roleName}</b>. An invitation to sign in has been sent to <b>{email}</b>.
				</div>
			)
		} catch (e) {
			const conflict = e?.graphQLErrors?.find((err) => err?.extensions?.code === 'EMPLOYER_ALREADY_EXISTS')
			if (conflict) {
				// The dialog below is the answer; the API layer must not add a toast on top of it.
				markNotified(e)
				this.handleExistingEmployer(conflict)
				return
			}

			console.error(e)
			notifyStore.error(e)
		}
	}

	// Turns the collision into something the admin can act on.
	//
	// `hasInternalRole` splits the two cases, because the right next step differs.
	// Already on the screen: nothing to do but go look at their row. Not on the
	// screen: they exist without an internal role, which is the actual bug the
	// admin walked into, and assigning one is the fix.
	@action
	handleExistingEmployer = (conflict) => {
		const { employerId, hasInternalRole } = conflict?.extensions || {}
		const displayName = employerNameFromConflictMessage(conflict?.message)

		if (hasInternalRole) {
			notifyStore.info(`${displayName} is already on User Roles. Search for them on this screen to change their access.`, {
				title: 'Already on this list',
				showDialog: true,
				okText: 'OK',
				onOk: (key) => notifyStore.closeDialog(key),
				onClose: (key) => notifyStore.closeDialog(key),
			})
			return
		}

		notifyStore.open(`${displayName} is already in Maestro but doesn't have an access level yet — that's why they're not on this list. Assign one now?`, {
			title: 'Assign access level',
			showDialog: true,
			okText: 'Assign role',
			cancelText: 'Cancel',
			onCancel: (key) => notifyStore.closeDialog(key),
			onClose: (key) => notifyStore.closeDialog(key),
			onOk: async (key) => {
				notifyStore.closeDialog(key)
				// Open assign dialog immediately with conflict metadata so the UI
				// does not sit on the Add user form while the profile fetch runs.
				const [firstName, ...rest] = displayName.split(' ')
				runInAction(() => {
					this.openAssignRoleDialog({
						id: employerId,
						firstName: firstName || '',
						lastName: rest.join(' ') || '',
						fullName: displayName,
						roles: [],
					})
				})
				await this.hydrateAssignRolePopupData(employerId)
			},
		})
	}

	// The existing record is by definition not in `this.recruiters` - the list that
	// hid it is the same list this screen renders - so fetch it by id rather than
	// looking it up locally.
	@action
	hydrateAssignRolePopupData = async (employerId) => {
		try {
			const { data } = await apolloClient().query({
				query: GET_RECRUITER_PROFILE,
				variables: { id: employerId },
				fetchPolicy: 'network-only',
			})

			const employer = data?.recruiterProfile
			if (!employer?.id) return

			runInAction(() => {
				this.updatePopupData(employer)
			})
		} catch (e) {
			console.error(e)
		}
	}

	@action
	openAssignRoleForExisting = async (employerId) => {
		try {
			const { data } = await apolloClient().query({
				query: GET_RECRUITER_PROFILE,
				variables: { id: employerId },
				fetchPolicy: 'network-only',
			})

			const employer = data?.recruiterProfile
			if (!employer) {
				notifyStore.error("We couldn't open that user. Please refresh and try again.")
				return
			}

			runInAction(() => {
				this.openAssignRoleDialog(employer)
			})
		} catch (e) {
			console.error(e)
			notifyStore.error(e)
		}
	}

	// Sends (or re-sends) the Maestro welcome email carrying a fresh temporary
	// password.
	//
	// Confirmed rather than immediate, because this is destructive in a way the
	// icon does not suggest: the server replaces the user's password, so anyone who
	// already had working credentials loses them. The wording says so plainly - an
	// admin clicking "Resend" on a colleague who simply forgot to log in should
	// understand they are about to reset that colleague's password.
	@action
	// `onDone` lets the detail page refresh itself instead of the table. Without it
	// a resend fired from a user's own page would refetch the list behind them and
	// leave the page they are looking at showing the old invite date.
	resendInvite = async (rowData, { onDone } = {}) => {
		const fullName = createFullName(rowData?.firstName, rowData?.lastName)
		const neverInvited = getAccessState(rowData?.user) === ACCESS_STATE.notInvited

		const message = neverInvited
			? `Send ${fullName} their sign-in details? They'll get an email with a temporary password.`
			: `Send ${fullName} a new invitation? This creates a new temporary password, and their current one will stop working.`

		notifyStore.open(message, {
			title: neverInvited ? 'Send invite' : 'Resend invite',
			showDialog: true,
			okText: neverInvited ? 'Send invite' : 'Resend invite',
			cancelText: 'Cancel',
			onCancel: (key) => notifyStore.closeDialog(key),
			onClose: (key) => notifyStore.closeDialog(key),
			onOk: async (key) => {
				notifyStore.closeDialog(key)
				await this.confirmResendInvite(rowData, { onDone })
			},
		})
	}

	@action
	confirmResendInvite = async (rowData, { onDone } = {}) => {
		if (this.resendingInviteFor) return

		this.resendingInviteFor = rowData?.id
		try {
			await apolloClient().mutate({
				mutation: RESEND_MAESTRO_INVITE,
				variables: { employerId: rowData?.id },
			})

			// Refetch rather than patching the row: the invite fields read off `user`
			// all move together on a successful send. The bare call keeps whatever
			// keyword the list is filtered by - see fetchRecruiters.
			await (onDone ? onDone() : this.fetchRecruiters())

			notifyStore.success(
				<div>
					Invitation sent to <b>{rowData?.email}</b>.
				</div>
			)
		} catch (e) {
			console.error(e)
			// Surfaced, not swallowed. The expected failures here - not an admin, or
			// the cooldown after a send that already went out - both need to reach the
			// person who pressed the button, or it looks like nothing happened.
			notifyStore.error(e)
		} finally {
			this.resendingInviteFor = null
		}
	}

	@action
	fetchAllRecruiters = async () => {
		const response = await apolloClient().query({
			query: GET_RECRUITER_LIST,
		})
		const defaultData = response?.data?.recruiters?.nodes?.map((item) => ({ ...item, label: createFullName(item.firstName, item.lastName), value: item.id }))
		this.employerOptions = [...defaultData]
	}

	@action
	fetchRecruiters = async (fullName, additionalFilter = {}) => {
		// Strictly undefined means "refetch whatever is on screen" - the caller is an
		// action finishing, not the search box. An empty string is a real value: it
		// is what clearing the search sends, and it must not be mistaken for this.
		if (fullName !== undefined) this.lastKeyword = fullName
		const keyword = fullName === undefined ? this.lastKeyword : fullName

		const sortBy = (this.sortOrder && {
			field: this.sortField || 'fullName',
			order: this.sortOrder || 'desc',
		}) || {
			field: 'fullName',
			order: 'asc',
		}

		const filter = {
			...this.filterFields,
			...(keyword && { keyword }),
			...additionalFilter,
		}

		const response = await apolloClient().query({
			query: GET_RECRUITER_LIST,
			variables: {
				first: INITIAL_TABLE_PAGE_SIZE,
				sortBy,
				filter,
			},
		})

		this.recruiters = response?.data?.recruiters?.nodes?.map((item) => ({
			...item,
			fullName: createFullName(item.firstName, item.lastName),
		}))
		this.pageInfo = response?.data?.recruiters?.pageInfo
		this.outerTotalCount = response?.data?.recruiters?.totalCount || 0
	}

	@action
	onScrollToEnd = async (batchSize = LIMIT_DATA_INNER_TABLE, pageOptions = {}) => {
		// Paging reuses this fetch: `after` starts the slice anywhere (the cursor
		// is base64 of a row offset), `replace` swaps the rows instead of appending.
		const { after: pageAfter, replace: replaceRows } = pageOptions
		const sortBy = (this.sortOrder && {
			field: this.sortField,
			order: this.sortOrder,
		}) || {
			field: 'fullName',
			order: 'asc',
		}

		const filter = Object.keys(this.filterFields).length && this.filterFields

		const response = await apolloClient().query({
			query: GET_RECRUITER_LIST,
			variables: {
				first: batchSize,
				sortBy,
				after: replaceRows ? pageAfter : this.endCursor,
				filter: { ...filter },
			},
		})

		this.pageInfo = response?.data?.recruiters?.pageInfo
		this.recruiters = replaceRows
			? response?.data?.recruiters?.nodes?.map((item) => ({ ...item, fullName: createFullName(item.firstName, item.lastName) }))
			: [...this.recruiters, ...response?.data?.recruiters?.nodes?.map((item) => ({ ...item, fullName: createFullName(item.firstName, item.lastName) }))]
		this.outerTotalCount = response?.data?.recruiters?.totalCount || 0
	}

	@action
	startImpersonation = async (recruiterId) => {
		// Fetch recruiter details before starting impersonation
		const response = await apolloClient().query({
			query: GET_RECRUITER_LIST,
			variables: {
				filter: { id: recruiterId },
			},
		})

		const recruiter = response?.data?.recruiters?.nodes?.[0]
		if (recruiter) {
			this.impersonatedRecruiter = recruiter
			localStorage.setItem('impersonateRecruiterId', recruiterId)
			localStorage.setItem('impersonatedRecruiter', JSON.stringify(recruiter))
			this.isImpersonating = true
			await routerStore.goPage(PATHS.recruiter.home)
			window.location.reload()
		}
	}

	@action
	stopImpersonation = () => {
		localStorage.removeItem('impersonateRecruiterId')
		localStorage.removeItem('impersonatedRecruiter')
		this.impersonatedRecruiter = null
		this.isImpersonating = false
		window.location.reload()
	}

	@action
	initializeImpersonation = () => {
		const impersonateId = localStorage.getItem('impersonateRecruiterId')
		const savedRecruiter = localStorage.getItem('impersonatedRecruiter')
		if (impersonateId && savedRecruiter) {
			this.isImpersonating = true
			this.impersonatedRecruiter = JSON.parse(savedRecruiter)
		}
	}
}

export const recruiterRoleStore = new RecruiterRoleStore()
